import {
  ACCOUNT_CODE_VERIFIER,
  ACCOUNT_OATH_STATE
} from '@api/accounts/auth/consts'
import {
  generateNonce,
  generateState,
  getHomeUrl,
  getRedirectUrl
} from './helpers'
import { generateCodeChallenge, generateCodeVerifier } from './pkce'
import axios from 'axios'

// TODO - remove hardcoded credentials once we have these being set in the deployments
const getConfig = () => {
  const shopId = process.env.SHOP_ID || '1360134207'
  const clientId =
    process.env.ACCOUNTS_CLIENT_ID || 'shp_4b987526-a6ca-4c02-9728-8107f69552c9'
  const authorizationRequestBase = `https://shopify.com/authentication/${shopId}`

  return {
    clientId,
    authorizationRequestBase
  }
}

export interface AccessTokenResponse {
  access_token: string
  expires_in: number
  id_token?: string
  refresh_token: string
}

export const getAccessToken = async (code: string, state: string) => {
  const { authorizationRequestBase, clientId } = getConfig()

  const savedState: string | null = localStorage.getItem(ACCOUNT_OATH_STATE)

  if (state !== savedState) {
    throw new Error(`Invalid state (local state = ${savedState})`)
  }

  const body = new URLSearchParams()

  // Public Client
  const codeVerifier: string | null = localStorage.getItem(
    ACCOUNT_CODE_VERIFIER
  )

  const searchParams: Record<string, string> = {
    grant_type: 'authorization_code',
    client_id: clientId,
    redirect_uri: getRedirectUrl(),
    code,
    code_verifier: codeVerifier!
  }

  Object.entries(searchParams).forEach(([key, value]) =>
    body.append(key, value)
  )

  const headers = {
    'content-type': 'application/x-www-form-urlencoded'
  }

  const response = await axios.post(
    `${authorizationRequestBase}/oauth/token`,
    body,
    {
      headers
    }
  )

  return response.data as AccessTokenResponse
}

export const refreshAccessToken = async (refreshToken: string) => {
  const { authorizationRequestBase, clientId } = getConfig()

  const body = new URLSearchParams()

  body.append('grant_type', 'refresh_token')
  body.append('client_id', clientId)
  body.append('refresh_token', refreshToken)

  const headers = {
    'content-type': 'application/x-www-form-urlencoded'
  }

  const response = await axios.post(
    `${authorizationRequestBase}/oauth/token`,
    body,
    {
      headers
    }
  )

  return response.data as AccessTokenResponse
}

export const getAuthUrl = async () => {
  const { clientId, authorizationRequestBase } = getConfig()

  // PKCE
  const verifier = generateCodeVerifier()
  const challenge = await generateCodeChallenge(verifier)
  localStorage.setItem(ACCOUNT_CODE_VERIFIER, verifier)

  const state = generateState()
  localStorage.setItem(ACCOUNT_OATH_STATE, state)

  const authorizationRequestUrl = new URL(
    `${authorizationRequestBase}/oauth/authorize`
  )

  authorizationRequestUrl.searchParams.append('code_challenge', challenge)
  authorizationRequestUrl.searchParams.append('code_challenge_method', 'S256')

  const searchParams: Record<string, string> = {
    scope: 'openid email customer-account-api:full',
    client_id: clientId,
    response_type: 'code',
    redirect_uri: getRedirectUrl(),
    state,
    nonce: generateNonce(16),
    code_challenge: challenge,
    code_challenge_method: 'S256'
  }

  Object.entries(searchParams).forEach(([key, value]) =>
    authorizationRequestUrl.searchParams.append(key, value)
  )

  return authorizationRequestUrl.toString()
}

export const getLogoutUrl = (idToken: string) => {
  const { authorizationRequestBase } = getConfig()

  const logoutUrl = new URL(`${authorizationRequestBase}/logout`)

  logoutUrl.searchParams.append('id_token_hint', idToken)
  logoutUrl.searchParams.append('post_logout_redirect_uri', getHomeUrl())

  return logoutUrl.toString()
}
